/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useEffect, useState } from "react";
import type { User, Session, AuthChangeEvent } from "@supabase/supabase-js";
import { supabase } from "~/src/integrations/supabase/client";
import { debugLog } from "~/src/lib/debug";
import { LoadingSpinner } from "@/components/ui/loading-spinner";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

type ProfileEvent = AuthChangeEvent | "INITIAL_SESSION";

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  const ensureUserProfile = async (user: User | null, event?: ProfileEvent) => {
    if (!user) return;

    const profileSyncEvents: Set<ProfileEvent> = new Set([
      "INITIAL_SESSION",
      "SIGNED_IN",
      "TOKEN_REFRESHED",
    ]);

    if (event && !profileSyncEvents.has(event)) {
      return;
    }

    const sanitizeUsername = (value?: string | null): string | undefined => {
      if (!value) return undefined;
      const normalized = value
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9_]/g, '_')
        .replace(/_+/g, '_')
        .replace(/^_+|_+$/g, '');

      if (normalized.length < 3) {
        return undefined;
      }

      return normalized.slice(0, 30);
    };

    const metadata = user.user_metadata ?? {};

    try {
      // Check if user already exists — only set username on first profile creation
      // to avoid 409 conflicts when derived username collides with another user
      const { data: existingUser } = await supabase
        .from("users")
        .select("id, username")
        .eq("id", user.id)
        .maybeSingle();

      const profileData: Record<string, string | null | undefined> = {
        id: user.id,
        email: user.email ?? undefined,
        last_login: new Date().toISOString(),
      };

      if (metadata.full_name) {
        profileData.full_name = metadata.full_name;
      }

      // Only set username for new users who don't have one yet
      if (!existingUser) {
        const derivedUsername =
          metadata.username ??
          metadata.user_name ??
          metadata.preferred_username ??
          metadata.full_name ??
          (user.email ? user.email.split("@")[0] : undefined);

        const usernameCandidates = [
          derivedUsername,
          metadata.full_name,
          user.email,
          user.id,
        ];
        const safeUsername = usernameCandidates
          .map((candidate) => (typeof candidate === 'string' ? sanitizeUsername(candidate) : undefined))
          .find((candidate): candidate is string => Boolean(candidate));

        if (safeUsername) {
          profileData.username = safeUsername;
        }
      }

      const { error } = await supabase.from("users").upsert(profileData, {
        onConflict: "id",
        ignoreDuplicates: false,
      });
      if (error) {
        // Username is optional. A derived-name collision must not prevent the
        // fallback account profile from being created for private features.
        if (error.code === '23505' && profileData.username) {
          const { username: _username, ...withoutUsername } = profileData;
          const { error: retryError } = await supabase.from("users").upsert(withoutUsername, { onConflict: "id", ignoreDuplicates: false });
          if (retryError) console.error("Error ensuring user profile:", retryError);
        } else {
          console.error("Error ensuring user profile:", error);
        }
      }
    } catch (err) {
      console.error("Error ensuring user profile:", err);
    }
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      console.error("Error signing out:", error);
    }
  };

  useEffect(() => {
    debugLog('🔐 Setting up auth state listener...');

    let mounted = true;
    let receivedAuthEvent = false;
    const syncingUsers = new Set<string>();
    const pendingSyncs = new Set<ReturnType<typeof setTimeout>>();

    const handleSessionChange = (session: Session | null, event: ProfileEvent) => {
      if (!mounted) return;
      debugLog('🔐 Auth session update:', {
        event,
        hasSession: !!session,
        timestamp: new Date().toISOString()
      });

      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);

      // Auth callbacks run while the SDK holds its session lock. Profile queries
      // must start after it releases that lock, and must never block sign-in.
      if (!session?.user || syncingUsers.has(session.user.id)) return;
      const sessionUser = session.user;
      syncingUsers.add(sessionUser.id);
      const timer = setTimeout(() => {
        pendingSyncs.delete(timer);
        if (!mounted) return;
        void ensureUserProfile(sessionUser, event).finally(() => syncingUsers.delete(sessionUser.id));
      }, 0);
      pendingSyncs.add(timer);
    };

    // Set up auth state listener
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      receivedAuthEvent = true;
      handleSessionChange(session, event);
    });

    // Check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!receivedAuthEvent) handleSessionChange(session, "INITIAL_SESSION");
    }).catch(() => {
      if (!receivedAuthEvent) handleSessionChange(null, "INITIAL_SESSION");
    });

    return () => {
      debugLog('🔐 Cleaning up auth listener...');
      mounted = false;
      pendingSyncs.forEach(clearTimeout);
      subscription.unsubscribe();
    };
  }, []);

  // Show loading spinner while auth is initializing
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <LoadingSpinner size="lg" text="Loading..." />
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, session, loading, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};
